<template>
  <article
    v-if="tile"
    class="head-to-head h2h-summary"
    :class="[
      `head-to-head--style-${HeadToHeadCardStyle.SummaryWithChart}`,
      {
        'h2h-summary--settled': summary.state === constants.MARKET_SETTLED,
        'h2h-summary--closed': summary.state === constants.MARKET_CLOSED,
      },
    ]"
    @pointerenter="queueHoverAnimation"
    @pointerleave="cancelHoverAnimation"
  >
    <header class="h2h-summary__header">
      <div>
        <router-link :to="marketLink">{{ tile.title }}</router-link>
      </div>
      <span v-if="tile.date || summary.state !== constants.MARKET_OPEN">{{
        summary.state === constants.MARKET_CLOSED
          ? 'Awaiting result'
          : summary.state === constants.MARKET_SETTLED
            ? 'Closed'
            : tile.date
      }}</span>
    </header>
    <h3 class="h2h-summary__headline">
      <template v-if="summary.state === constants.MARKET_SETTLED">
        <template v-if="summary.winner"
          ><span class="h2h-summary__contract-color" :style="contractStyle(summary.winner)">{{
            summary.winner.name
          }}</span>
          defeats<br />{{ summary.loser.name }}</template
        >
        <template v-else-if="summary.neitherWins"
          >Neither {{ tile.left.name }} nor<br />{{ tile.right.name }} wins</template
        >
        <template v-else>{{ tile.left.name }} vs<br />{{ tile.right.name }}</template>
      </template>
      <template v-else-if="summary.leader"
        ><span class="h2h-summary__contract-color" :style="contractStyle(summary.leader)">{{
          summary.leader.name
        }}</span>
        {{ summary.verb }}<br />{{ summary.trailer.name }} by {{ summary.difference }}</template
      >
      <template v-else-if="summary.tied"
        >{{ tile.left.name }} is tied<br />with {{ tile.right.name }}</template
      >
      <template v-else>{{ tile.left.name }} vs<br />{{ tile.right.name }}</template>
    </h3>
    <div ref="chart" class="h2h-summary__chart">
      <svg
        data-history-chart
        :data-animation-progress="animationProgress"
        viewBox="-3 0 365 120"
        role="img"
        :aria-label="chartDescription"
      >
        <template v-if="hasHistory">
          <defs>
            <clipPath :id="chartClipId">
              <rect x="-3" y="-10" :width="chartWidth * animationProgress + 3" height="150" />
            </clipPath>
          </defs>
          <line
            x1="0"
            :y1="y(chartMidPrice)"
            :x2="chartWidth"
            :y2="y(chartMidPrice)"
            class="h2h-summary__reference"
          />
          <g
            v-for="(series, index) in summary.series"
            :key="series.id"
            class="h2h-summary__contract-color"
            :style="contractStyle(series)"
          >
            <path
              v-if="series.hasHistory"
              :data-series="series.id"
              :clip-path="`url(#${chartClipId})`"
              :d="chartPath(series)"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linejoin="round"
            />
            <template v-if="markers[index]">
              <circle
                :cx="markers[index].x"
                :cy="y(markers[index].price)"
                r="5.5"
                fill="currentColor"
              />
              <text
                :x="endpointLabelX(markers[index])"
                text-anchor="end"
                :y="endLabelY[index] + 5"
                fill="currentColor"
                class="h2h-summary__end-price"
              >
                {{ series.display }}
              </text>
            </template>
          </g>
        </template>
        <text
          v-else
          x="179.5"
          y="60"
          text-anchor="middle"
          dominant-baseline="middle"
          class="h2h-summary__empty"
        >
          Not enough price history
        </text>
      </svg>
      <div class="h2h-summary__axis" :class="{ 'h2h-summary__axis--dated': axisDates }">
        <span
          >{{ summary.state === constants.MARKET_SETTLED ? '30 days out' : '30 days ago'
          }}<small v-if="axisDates">{{ axisDates.start }}</small></span
        >
        <span
          >{{ summary.state === constants.MARKET_SETTLED ? 'Close' : 'Today'
          }}<small v-if="axisDates">{{ axisDates.end }}</small></span
        >
      </div>
    </div>
    <div class="h2h-summary__contracts">
      <div
        v-for="series in summary.series"
        :key="series.id"
        class="h2h-summary__contract"
        :class="{
          'h2h-summary__contract--winner':
            summary.state === constants.MARKET_SETTLED && series.isWinner,
          'h2h-summary__contract--defeated': series.isDefeated,
        }"
      >
        <router-link
          class="h2h-summary__identity"
          :to="{ ...marketLink, query: { contractId: series.id } }"
          :aria-label="identityLabel(series)"
        >
          <img
            v-if="series.image && !failedImages[series.id]"
            :src="series.image"
            alt=""
            @error="failedImages[series.id] = true"
          />
          <span
            v-else
            class="h2h-summary__initials"
            :style="{ background: series.color, color: series.ink }"
            >{{ initials(series.name) }}</span
          >
          <div
            class="h2h-summary__identity-text"
            :data-name="`${series.name}${series.partyLabel ? ` (${series.partyLabel})` : ''}`"
          >
            <div class="h2h-summary__identity-content">
              <span class="h2h-summary__name"
                >{{ series.name }}{{ series.partyLabel ? ` (${series.partyLabel})` : '' }}</span
              >
              <span
                v-if="summary.state === constants.MARKET_CLOSED"
                class="h2h-summary__change h2h-summary__change--neutral"
                >Trading closed</span
              >
              <span
                v-else-if="summary.state === constants.MARKET_SETTLED"
                class="h2h-summary__result"
              >
                <template v-if="series.isWinner"
                  ><span class="h2h-summary__winner-icon" aria-hidden="true"></span>Winner</template
                >
                <template v-else-if="series.isDefeated">Defeated</template>
              </span>
              <span
                v-else-if="series.weeklyChange !== null"
                class="h2h-summary__change"
                :class="{
                  'h2h-summary__change--up': series.weeklyChange > 0,
                  'h2h-summary__change--down': series.weeklyChange < 0,
                  'h2h-summary__change--neutral': series.weeklyChange === 0,
                }"
              >
                <span v-if="series.weeklyChange === 0">No change</span>
                <template v-else
                  ><market-change-price
                    :price="series.weeklyChange"
                    :symbol="tile.currentPriceSymbol"
                  /><span>this week</span></template
                >
              </span>
            </div>
          </div>
        </router-link>
        <button
          v-if="summary.state !== constants.MARKET_SETTLED"
          class="h2h-summary__buy h2h-summary__contract-color"
          :style="contractStyle(series)"
          :disabled="isSummaryTradeDisabled(series)"
          @click="tradeSummary(series, $event)"
        >
          Buy {{ series.buyName }}{{ series.buyDisplay ? ` ${series.buyDisplay}` : '' }}
        </button>
      </div>
    </div>
    <router-link
      v-if="summary.state === constants.MARKET_SETTLED"
      class="h2h-summary__race-link h2h-summary__contract-color"
      :style="raceLinkStyle"
      :to="marketLink"
      >See how the Race Played Out<svg viewBox="0 0 16 16" aria-hidden="true">
        <path d="m6 3 5 5-5 5" /></svg
    ></router-link>
  </article>
  <div v-else class="head-to-head h2h-summary" role="status">
    <p>Head to Head is unavailable for this market (misconfigured contracts).</p>
  </div>
</template>
<script src="./h2h-summary-with-chart.js"></script>
<style src="./h2h-summary-with-chart.scss" lang="scss"></style>
